import React, { ElementRef, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { INSIGHT_VIEWS } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { okrsInsightsSelector } from 'selectors/okrsInsightsSelector';
import { okrsPageSelector } from 'selectors/okrsPage';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { sendCreatedKREvent, sendCreatedObjectiveEvent } from 'constants/mixpanelHelper';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { AgGridAddNewGoalObjectiveSplitButton } from '../../AgGridAddNewGoalObjectiveSplitButton';
import { GroupByDropdown } from '../../Insights';
import { useOKRGroupByViewType } from '../hooks/useOKRGroupByViewType';
import { useOKRManageViewType } from '../hooks/useOKRManageViewType';
import { OKRManageViews } from './OKRManageViews/OKRManageViews';

interface Props {
  showHierarchyToggle: boolean;
  ellipsisMenuOptions: Array<{
    label: string;
    onClick: () => void;
    Icon?: React.FC;
    type?: 'subMenu' | 'default' | 'radio' | 'radioSubMenu';
    subOptions?: Array<{
      label: string;
      onClick: () => void;
      Icon?: React.FC;
      description?: string;
    }>;
    value?: string;
  }>;
  refetchCascadedViewData: () => void;
  showArchivedGoals: boolean;
  gridRef: ElementRef<typeof AgGridReact>;
}

const ManageGoalsFilterBar = ({
  showHierarchyToggle,
  ellipsisMenuOptions,
  refetchCascadedViewData,
  showArchivedGoals,
  gridRef,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { selectedTimelines } = useSelector(okrsPageSelector);
  const page = useViewPageName();
  const sideNavSelectedCohort = useSelectedView();
  const selectedCohortId = sideNavSelectedCohort?.id;
  const { viewType } = useSelector(okrsInsightsSelector);
  const { viewType: OKRManageView } = useOKRManageViewType();

  const { setGroupByViewType } = useOKRGroupByViewType();

  // if it's not my goals and is not card, tree view
  const showGroupByDropdown = selectedCohortId !== -12 && OKRManageView === 'list';

  const showSearch = OKRManageView === 'list' || OKRManageView === 'listWithHierarchy';

  const changeCurrentView = (viewType) => {
    // dispatch(setViewType(viewType));

    setGroupByViewType(viewType);
  };

  useEffect(() => {
    if (selectedCohortId === -12) {
      // dispatch(setViewType(INSIGHT_VIEWS[1].value));
      setGroupByViewType(INSIGHT_VIEWS[1].value);
    }
  }, [
    // dispatch,
    selectedCohortId,
    setGroupByViewType,
    viewType,
  ]);

  return (
    <Box mb="12px">
      <Flex ml="auto" alignItems="center">
        {showSearch && (
          <Box w="620px">
            <AgGridSearch
              // @ts-expect-error still some error in ref
              gridRef={gridRef}
              placeholder="Search across title, owner, progress etc."
              cohortId={sideNavSelectedCohort?.id}
              //   @ts-expect-error css?
              inputProps={{ minWidth: '100px' }}
            />
          </Box>
        )}

        <Flex
          w="100%"
          sx={{
            span: {
              marginLeft: isMobileView() || showHierarchyToggle ? 0 : 'auto',
            },
          }}
          alignItems="center"
          justifyContent="flex-end"
          marginLeft={isMobileView() ? '10px' : '0px'}
          // mr={isMobileView ? 0 : '16px'}
        >
          <AgGridAddNewGoalObjectiveSplitButton
            onAddKRClick={() => {
              dispatch(
                // @ts-expect-error error pro max
                openCreateOKRPanel({
                  selectedObjective: null,
                  selectedTimeBoundPeriod: selectedTimelines?.[0],
                  okrPanelType: OKR_TYPES.KEY_RESULT,
                  onKeyResultCreation(okrDetails) {
                    sendCreatedKREvent({
                      eventProperties: {
                        source: 'Add New Button',
                        page,
                        goalId: okrDetails?.uuid,
                      },
                    });
                    refetchCascadedViewData();
                  },
                  onObjectiveCreation(okrDetails) {
                    sendCreatedObjectiveEvent({
                      eventProperties: {
                        source: 'Add New Button',
                        page,
                        objectiveId: okrDetails?.uuid,
                        description: okrDetails?.description,
                        level: okrDetails?.objectiveLevel,
                        type: okrDetails?.type,
                        timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                      },
                    });
                    refetchCascadedViewData();
                  },
                  history,
                  location,
                })
              );
            }}
            onAddObjectiveClick={() => {
              dispatch(
                // @ts-expect-error error pro max v2
                openCreateOKRPanel({
                  selectedObjective: null,
                  selectedTimeBoundPeriod: selectedTimelines?.[0],
                  okrPanelType: OKR_TYPES.OBJECTIVE,
                  onKeyResultCreation(okrDetails) {
                    sendCreatedKREvent({
                      eventProperties: {
                        source: 'Add New Button',
                        page,
                        goalId: okrDetails?.uuid,
                      },
                    });
                    refetchCascadedViewData();
                  },
                  onObjectiveCreation(okrDetails) {
                    sendCreatedObjectiveEvent({
                      eventProperties: {
                        source: 'Add New Button',
                        page,
                        objectiveId: okrDetails?.uuid,
                        description: okrDetails?.description,
                        level: okrDetails?.objectiveLevel,
                        type: okrDetails?.type,
                        timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                      },
                    });
                    refetchCascadedViewData();
                  },
                  history,
                  location,
                })
              );
            }}
          />
        </Flex>

        {!isMobileView() && (
          <Flex mr={2}>
            <OKRManageViews />

            <AgGridEllipsisMenu
              options={ellipsisMenuOptions}
              selectedRadioValue={showArchivedGoals ? 'showArchivedGoals' : null}
            />
          </Flex>
        )}
      </Flex>
      {showGroupByDropdown && (
        <Flex alignItems="center" height="64px">
          <Flex>
            <TextBodyMedium color="text.g3" bold mr={4}>
              Group by :
            </TextBodyMedium>
            <GroupByDropdown selectedOption={viewType} handleOptionChange={changeCurrentView} />
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

export { ManageGoalsFilterBar };
